import { useCallback, useMemo } from 'react';
import {
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import {
  DRILLDOWN_VIEWS,
  DRILLDOWN_ORIGINS,
  changeDrilldownSearch,
  changeDrilldownSort,
  changeDrilldownView,
  normalizeDrilldownSearch,
  parseDrilldownBucket,
  type CustomerSort,
  type DrilldownOrigin,
  type DrilldownState,
  type OrderSort,
  type RepurchaseSegment,
  type DrilldownView,
} from '@/lib/orders-drilldown';

const drilldownSearchParams = {
  drill: parseAsStringLiteral(DRILLDOWN_ORIGINS),
  drillBucket: parseAsString,
  drillPage: parseAsInteger.withDefault(1),
  drillView: parseAsStringLiteral(DRILLDOWN_VIEWS),
  drillQ: parseAsString,
  drillSort: parseAsString,
};

export interface OrdersDrilldownStateApi {
  state: DrilldownState;
  /**
   * Opens the drill-down for an origin (and optionally a chart bucket) on
   * page 1, with the default view, no search and the default sort.
   */
  open: (origin: DrilldownOrigin, bucket?: string) => void;
  close: () => void;
  setPage: (page: number) => void;
  /** Back to page 1; bucket, view and search are kept. */
  setSegment: (segment: RepurchaseSegment) => void;
  /** Back to page 1; segment, bucket and search are kept. */
  setView: (view: DrilldownView) => void;
  /** Back to page 1. */
  setSearch: (search: string | null) => void;
  /** Back to page 1. */
  setSort: (sort: OrderSort | CustomerSort) => void;
}

/** The URL only carries what differs from the defaults (page 1, orders view). */
function toParams(next: DrilldownState) {
  return {
    drillPage: next.page <= 1 ? null : next.page,
    drillView: next.view === 'orders' ? null : next.view,
    drillQ: normalizeDrilldownSearch(next.search),
    drillSort: next.sort,
  };
}

export function useOrdersDrilldownState(): OrdersDrilldownStateApi {
  const [params, setParams] = useQueryStates(drilldownSearchParams, {
    history: 'replace',
  });

  const state = useMemo<DrilldownState>(
    () => ({
      origin: params.drill,
      bucket: params.drillBucket,
      page: params.drillPage,
      view: params.drillView ?? 'orders',
      search: params.drillQ,
      sort: params.drillSort,
    }),
    [params]
  );

  const open = useCallback(
    (origin: DrilldownOrigin, bucket?: string) => {
      void setParams({
        drill: origin,
        drillBucket: parseDrilldownBucket(bucket ?? null),
        drillPage: null,
        drillView: null,
        drillQ: null,
        drillSort: null,
      });
    },
    [setParams]
  );

  const close = useCallback(() => {
    void setParams({
      drill: null,
      drillBucket: null,
      drillPage: null,
      drillView: null,
      drillQ: null,
      drillSort: null,
    });
  }, [setParams]);

  const setPage = useCallback(
    (page: number) => {
      void setParams({ drillPage: page <= 1 ? null : page });
    },
    [setParams]
  );

  const setSegment = useCallback(
    (segment: RepurchaseSegment) => {
      void setParams({
        drill: segment,
        drillPage: null,
      });
    },
    [setParams]
  );

  const setView = useCallback(
    (view: DrilldownView) => {
      void setParams(toParams(changeDrilldownView(state, view)));
    },
    [setParams, state]
  );

  const setSearch = useCallback(
    (search: string | null) => {
      void setParams(toParams(changeDrilldownSearch(state, search)));
    },
    [setParams, state]
  );

  const setSort = useCallback(
    (sort: OrderSort | CustomerSort) => {
      void setParams(toParams(changeDrilldownSort(state, sort)));
    },
    [setParams, state]
  );

  return {
    state,
    open,
    close,
    setPage,
    setSegment,
    setView,
    setSearch,
    setSort,
  };
}
